import { Injectable, OnDestroy } from "@angular/core";
import { BehaviorSubject, Subject, Observable } from "rxjs";
import { debounceTime, map, mergeMap, switchMap } from "rxjs/operators";
import { Announcement } from "../models/announcement/announcement";
import {
  EventCard,
  FilterAnnouncementType,
  NotificationItemType,
  TrafficActionType,
  UserRoles,
} from "../models/enums";
import { Event } from "../models/event/event";
import { EventsResponse } from "../models/event/events-response";
import { TrafficAction } from "../models/traffic-action/traffic-action";
import { AutoUnsubscribe } from "../shared/decorators";
import { AnnouncementService } from "./announcement.service";
import { AuthService } from "./auth.service";
import { EventService } from "./event.service";
import { FilterService } from "./filter.service";
import { NotificationService } from "./notification.service";
import { ReasonService } from "./reason.service";
import { PopupService } from "./popup.service";
import { TrafficActionService } from "./traffic-action.service";
import _ from "lodash";
import { HttpErrorResponse } from "@angular/common/http";
import { buildGenerateTrafficActionRequest } from "../shared/request-object-builder.utils";
import { TrainService } from "./train.service";
import { Filter } from "../models/event/filter";
export interface EventContainerHeaderState {
  headerText: string;
  forwardText: string;
  backButtonVisible: boolean;
  forwardButtonVisible: boolean;
}

/** Orchestrates event-container navigation, data loading, and create/edit flows. */
@Injectable({
  providedIn: "root",
})
@AutoUnsubscribe()
export class EventContainerService implements OnDestroy {
  public loading!: boolean;

  //#region UI navigation state
  public currentEventCardChange = new Subject<EventCard>();
  public createModeChange = new Subject<boolean>();
  public filterAnnouncementChange = new Subject<string>();
  public eventsResponseChange = new Subject<EventsResponse>();
  public trafficActionChange = new Subject<TrafficAction>();
  public readonly headerState$ = new BehaviorSubject<EventContainerHeaderState>({
    headerText: "",
    forwardText: "",
    backButtonVisible: false,
    forwardButtonVisible: false,
  });
  //#endregion

  public currentTrafficAction!: TrafficAction;

  private eventsResponse!: EventsResponse;
  private currentEventCard!: EventCard;
  private createMode = false;
  private _populateEventsSubject!: Subject<void>;

  private readonly filterAnnouncements: { [index: number]: string } = {
    [FilterAnnouncementType.Error]:
      "Något gick fel vid hämtning av händelser. Ladda om sidan och försök igen.",
    [FilterAnnouncementType.EventList]:
      "Ditt urval matchar inte några händelser.",
  };

  private readonly newEventTitle = "Ny händelse";
  private readonly newAnnouncementTitle = "Ny annonsering";
  private readonly newTrafficActionTitle = "Ny Trafikåtgärd";

  constructor(
    private filterService: FilterService,
    private authService: AuthService,
    private announcementService: AnnouncementService,
    private trafficActionService: TrafficActionService,
    private eventService: EventService,
    private reasonService: ReasonService,
    private popupService: PopupService,
    private trainService: TrainService,
    private notificationService: NotificationService
  ) {
    this.wireNotificationDeepLinks();
  }

  public getTrafficActionChangeTrigger(): Observable<TrafficAction> {
    return this.trafficActionChange;
  }

  public isCreateMode() {
    return this.createMode;
  }

  public setContainerHeader(
    currentEventCard: EventCard,
    title?: string,
    forwardButtonVisible?: boolean
  ) {
    switch (currentEventCard) {
      case EventCard.EventList:
        this.headerState$.next({
          headerText: title ?? "",
          backButtonVisible: false,
          forwardButtonVisible: true,
          forwardText: "Skapa nytt",
        });
        break;
      case EventCard.EventForm:
        this.headerState$.next({
          headerText: title ?? "",
          backButtonVisible: true,
          forwardButtonVisible: forwardButtonVisible ?? false,
          forwardText: "Loggar",
        });
        break;
      case EventCard.TrafficActionForm:
        this.headerState$.next({
          headerText: title ?? "",
          backButtonVisible: true,
          forwardButtonVisible: false,
          forwardText: "",
        });
        break;
      case EventCard.EventLog:
        this.headerState$.next({
          headerText: "Loggar",
          backButtonVisible: true,
          forwardButtonVisible: false,
          forwardText: "",
        });
        break;
      case EventCard.Announcement:
        this.headerState$.next({
          headerText: title ?? "",
          backButtonVisible: true,
          forwardButtonVisible: false,
          forwardText: "",
        });
        break;
      default:
        console.error("Not implemented");
        break;
    }
  }

  public returnToEventList() {
    this.eventService.selectEvent(null);
    this.goToEventList();
    this.eventService.emitTriggerForGetEvents(true);
  }

  public returnToEvent() {
    const selectedEvent = this.eventService.getSelectedEvent();
    if (selectedEvent?.id) {
      this.populateEvent(selectedEvent.id);
    } else {
      this.goToEventForm(selectedEvent);
    }
  }

  public returnToTrafficAction() {
    this.announcementService.selectAnnouncement(null);
    this.announcementService.selectedAnnouncementCategoryText = null;
    this.goToTrafficActionForm(
      this.trafficActionService.getSelectTrafficAction() ?? undefined
    );
  }

  public populateEvents() {
    if (!this._populateEventsSubject) {
      this._populateEventsSubject = new Subject();
      this._populateEventsSubject
        .pipe(
          debounceTime(300),
          switchMap(() => {
            this.loading = true;
            return this.eventService.getEvents();
          })
        )
        .subscribe(
          (response: EventsResponse) => {
            this.eventsResponse = response;
            this.eventsResponseChange.next(response);

            this.goToEventList(true);
            if (!response || response.events.length == 0) {
              this.emitFilterAnnouncement(FilterAnnouncementType.EventList);
            }

            this.loading = false;
          },
          (error: HttpErrorResponse) => {
            this.loading = false;
            this.goToEventList(true);
            this.emitFilterAnnouncement(FilterAnnouncementType.Error);
            this.popupService.setErrorPopup(error);
            console.error(error);
          }
        );
    }

    this._populateEventsSubject.next();
  }

  populateEvent(id: number) {
    this.loading = true;
    this.eventService.getEvent(id).subscribe(
      (event) => {
        this.eventService.selectEvent(event);
        this.goToEventForm(event, false);
        this.loading = false;
      },
      (error: HttpErrorResponse) => {
        this.loading = false;
        this.popupService.setErrorPopup(error);
        console.error(error);
      }
    );
  }

  populateTrafficAction(id: number) {
    this.loading = true;
    this.trafficActionService
      .getTrafficAction(id)
      .pipe(
        map(
          (trafficAction) => {
            this.trafficActionService.selectTrafficAction(trafficAction);
            this.goToTrafficActionForm(trafficAction);
            this.loading = false;

            return trafficAction;
          },
          (error: HttpErrorResponse) => {
            this.popupService.setErrorPopup(error);
          }
        ),
        mergeMap((trafficAction) =>
          this.eventService.getEvent(trafficAction.eventId!)
        ),
        map((event) => {
          this.eventService.selectEvent(event);
        })
      )
      .subscribe();
  }

  populateTrafficActionForAnnouncement(announcementId: number) {
    this.loading = true;
    this.trafficActionService
      .getTrafficActionForAnnouncement(announcementId)
      .pipe(
        map(
          (trafficAction) => {
            this.trafficActionService.selectTrafficAction(trafficAction);
            const announcementGroup = trafficAction.announcementCategoryGroups
              .find((m) => m.announcements.some((n) => n.id == announcementId))!;
            const announcement = announcementGroup.announcements.find((m) => m.id == announcementId)!;
            this.openExistingAnnouncement(announcement);
            this.loading = false;

            return trafficAction;
          },
          (error: HttpErrorResponse) => {
            this.popupService.setErrorPopup(error);
          }
        ),
        mergeMap((trafficAction) =>
          this.eventService.getEvent(trafficAction.eventId!)
        ),
        map((event) => {
          this.eventService.selectEvent(event);
        })
      )
      .subscribe();
  }

  initiateNewTrafficAction() {
    const selectedEvent = this.eventService.getSelectedEvent();
    const trafficAction = new TrafficAction({
      id: null as unknown as number,
      eventId: selectedEvent!.id!,
      reasonId: this.getTrafficActionReason(),
      templateType: TrafficActionType.OtherAdjustment,
      title: "",
      reminderInMinutes: null as unknown as number,
      filterGeneratedTrains: selectedEvent?.filterGeneratedTrains ?? [],
      trains: [],
      announcementCategoryGroups: [],
      filter: _.cloneDeep(selectedEvent!.filter),
    });
    trafficAction.filter.id = 0;
    const generateTrafficActionRequest =
      buildGenerateTrafficActionRequest(trafficAction);
    this.trafficActionService
      .generateTrafficAction(generateTrafficActionRequest)
      .subscribe((generatedTrafficAction) => {
        trafficAction.id = generatedTrafficAction.id;
        trafficAction.title = generatedTrafficAction.title;
        trafficAction.reasonId = generatedTrafficAction.reasonId;
        trafficAction.eventId = generatedTrafficAction.eventId;
        trafficAction.templateType = generatedTrafficAction.templateType;
        trafficAction.reminderInMinutes =
          generatedTrafficAction.reminderInMinutes;
        trafficAction.announcementCategoryGroups =
          generatedTrafficAction.announcementCategoryGroups;
        trafficAction.filter = generatedTrafficAction.filter;
        this.trafficActionChange.next(trafficAction);
      });
    this.trafficActionService.selectTrafficAction(trafficAction);
    this.goToTrafficActionForm(trafficAction);
  }

  initiateEditTrafficAction(trafficActionId: number, notificationId?: number) {
    this.trafficActionService.getTrafficAction(trafficActionId).subscribe(
      (trafficAction) => {
        if (notificationId) {
          this.notificationService.setReadStatus([notificationId]);
        }
        const filter: Filter = trafficAction.filter;
        this.trainService.getTrains(filter).subscribe(
          (trains) => {
            trafficAction.filterGeneratedTrains = trains;
            this.trafficActionService.selectTrafficAction(trafficAction);
            this.currentTrafficAction = _.cloneDeep(trafficAction);
            this.goToTrafficActionForm(trafficAction);
          },
          (error: HttpErrorResponse) => {
            this.popupService.setErrorPopup(error);
          }
        );
      },
      (error: HttpErrorResponse) => {
        this.popupService.setErrorPopup(error);
      }
    );
  }

  initiateNewEvent() {
    const filter = this.filterService.getFilterCopy();
    const event = new Event({
      id: null as unknown as number,
      comment: "",
      reasonId: -1,
      title: null as unknown as string,
      filter: filter,
      filterGeneratedTrains: [],
      trains: [],
      actions: [],
    });

    this.eventService.selectEvent(event);
    this.goToEventForm(event, true);
  }

  openAnnouncementForm() {
    this.goToAnnouncementForm();
  }

  openExistingAnnouncement(selectedAnnouncement: Announcement) {
    this.announcementService.emitSelectAnnouncementText(
      selectedAnnouncement?.categoryText!
    );
    this.announcementService.selectAnnouncement(selectedAnnouncement);
    this.goToAnnouncementForm();
  }

  afterSaveEvent(event: Event) {
    this.eventService.selectEvent(event);
    if (!event) {
      this.populateEvents();
    } else {
      this.goToEventForm(event, false);
    }
  }

  goToEventForm(event: Event | null, createMode?: boolean) {
    if (createMode != null && createMode != undefined) {
      this.switchCreateMode(createMode);
    }
    this.filterService.switchFilterState(
      EventCard.EventForm,
      true,
      event?.filter
    );
    this.switchEventCard(EventCard.EventForm);
    this.loading = false;
    this.setContainerHeader(
      this.currentEventCard,
      this.createMode ? this.newEventTitle : event?.title,
      !this.createMode
    );
  }

  goToTrafficActionForm(trafficAction?: TrafficAction | null) {
    this.filterService.switchFilterState(
      EventCard.TrafficActionForm,
      true,
      trafficAction?.filter
    );
    this.switchEventCard(EventCard.TrafficActionForm);
    this.loading = false;
    this.setContainerHeader(
      this.currentEventCard,
      trafficAction?.title ? trafficAction.title : this.newTrafficActionTitle
    );
  }

  private goToAnnouncementForm() {
    const selectedAnnouncement =
      this.announcementService.getSelectedAnnouncement();
    this.filterService.switchFilterState(
      EventCard.Announcement,
      true,
      selectedAnnouncement?.filter
    );
    this.switchEventCard(EventCard.Announcement);
    this.setContainerHeader(
      this.currentEventCard,
      selectedAnnouncement?.title
        ? selectedAnnouncement.title
        : this.newAnnouncementTitle
    );
  }

  private goToEventList(removeOldFilter = false) {
    this.filterService.switchFilterState(EventCard.EventList, removeOldFilter);
    this.switchCreateMode(false);
    this.switchEventCard(EventCard.EventList);
    this.setContainerHeader(
      this.currentEventCard,
      `Händelser (${this.eventsResponse?.totalItems ?? 0})`,
      this.hasWriteAccess()
    );
    if (removeOldFilter) {
      this.eventService.setPageNumber(0);
    }
  }

  private switchEventCard(newEventCard: EventCard) {
    this.currentEventCard = newEventCard;
    this.currentEventCardChange.next(this.currentEventCard);
  }

  private switchCreateMode(createMode: boolean) {
    this.createMode = createMode;
    this.createModeChange.next(this.createMode);
  }

  private emitFilterAnnouncement(
    filterAnnouncementType: FilterAnnouncementType
  ) {
    this.filterAnnouncementChange.next(
      this.filterAnnouncements[filterAnnouncementType] ?? ""
    );
  }

  private hasWriteAccess() {
    return this.authService.userHasMinimumRole(UserRoles.Write);
  }

  private getTrafficActionReason() {
    return this.reasonService.getMappedTrafficActionReason(
      this.eventService.getSelectedEvent()!.reasonId
    );
  }

  private wireNotificationDeepLinks() {
    this.notificationService.getOpenEventCardSubject().subscribe(
      (notification) => {
        const itemType: NotificationItemType =
          typeof notification.itemType === "string"
            ? (<any>NotificationItemType)[notification.itemType]
            : notification.itemType;
        switch (itemType) {
          case NotificationItemType.Event:
            this.populateEvent(notification.itemId);
            break;
          case NotificationItemType.TrafficAction:
            this.openTrafficActionFromNotification(notification.itemId);
            break;
          case NotificationItemType.Announcement:
            this.openAnnouncementFromNotification(notification.itemId);
            break;
        }
        this.notificationService.setReadStatus([notification.id]);
      }
    );
  }

  private openTrafficActionFromNotification(trafficActionId: number) {
    this.eventService.selectEvent(null);

    this.trafficActionService.getTrafficAction(trafficActionId).subscribe(
      (trafficAction) => {
        this.trafficActionService.selectTrafficAction(trafficAction);
        this.goToTrafficActionForm(trafficAction);
      },
      (error: HttpErrorResponse) => {
        this.popupService.setErrorPopup(error);
      }
    );
  }

  private openAnnouncementFromNotification(_announcementId: number) {
    this.eventService.selectEvent(null);
    this.trafficActionService.selectTrafficAction(null);
  }

  ngOnDestroy() {}
}
